import { createContext, useContext, useReducer, ReactNode } from 'react';
import {
  Product,
  CartItem,
  CartState,
  IDataOrder,
} from 'Interfaces/IContextProd';

// Actions
type CartAction =
  | { type: 'ADD_TO_CART'; payload: Product }
  | { type: 'REMOVE_FROM_CART'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'ADD_PROMOTION'; payload: { id: string; promotions: any } }
  | { type: 'REMOVE_PROMOTION'; payload: { id: string } }
  | { type: 'ADD_PAYMENT'; payload: { id: string, payments: any } }
  | { type: 'REMOVE_PAYMENT'; payload: { id: string } }
  | { type: 'ADD_DATA'; payload: IDataOrder }
  ;

// Estado inicial do retorno do contexto
const initialState: CartState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
  subtotal: 0,
  discount: '0',

  // data: undefined,
  // customer: undefined,
  data: {
    date: new Date().toISOString().split("T")[0],
    number_order: "", // ou null, se você preencher depois
    customer: undefined,
    seller: undefined
  },
};

function calculateTotals(items: CartItem[]) {
  const subtotal = items.reduce((sum, item) => {
    return sum + item.price * item.quantity;
  }, 0);
  const itemsWithSubtotal = items.map(item => {
    // subtotal pelo preço padrão
    const baseSubtotal = item.price * item.quantity;

    // subtotal pelos pagamentos (se existirem)
    const paymentsSubtotal = (item.payments || []).reduce((sum, p) => {
      const valor = Number(p.amount || 0);
      const taxa = valor * Number(p.taxa || 0) / 100;

      const taxaCredit = (p.down_payments || []).reduce((sum, val) => {
        const valor = Number(val.amount || 0);
        const taxa = Number(val.installment_interest_rate || 0) / 100;
        const result = valor * taxa;
        return sum + result
      }, 0)

      console.log('taxaCredit: ', taxaCredit);
      return sum + valor + taxa + taxaCredit;
    }, 0);

    // decide qual subtotal usar (pagamentos > base)
    const effectiveSubtotal = paymentsSubtotal > 0 ? paymentsSubtotal : baseSubtotal;

    return {
      ...item,
      baseSubtotal,
      paymentsSubtotal,
      effectiveSubtotal,
    };
  });

  // soma os subtotais "efetivos" de cada produto
  const totalPrice = itemsWithSubtotal.reduce((sum, item) => sum + item.effectiveSubtotal, 0);

  // desconto segue a mesma regra que antes (se aplicável)
  const discount = itemsWithSubtotal.reduce((sum, item) => {
    const valueDiscount = item.price;
    const discountStr = item.promotions?.personalized_discount || "0";
    const discountValue = valueDiscount * Number(discountStr) / 100;
    return sum + discountValue;
  }, 0);

  const totalItems = itemsWithSubtotal.reduce((sum, item) => sum + item.quantity, 0);

  return {
    items: itemsWithSubtotal,
    totalItems,
    subtotal,
    discount,
    totalPrice,
  };
}

// Reducer
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItem = state.items.find(item => item.id === action.payload.id);

      let newItems: CartItem[];
      if (existingItem) {
        newItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        newItems = [...state.items, { ...action.payload, quantity: 1 }];
      }

      return calculateTotals(newItems);
    }

    case 'REMOVE_FROM_CART': {
      const newItems = state.items.filter(item => item.id.toString() !== action.payload.toString());
      return calculateTotals(newItems);
    }

    case 'UPDATE_QUANTITY': {
      const newItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: Math.max(0, action.payload.quantity) }
          : item
      ).filter(item => item.quantity > 0);

      return calculateTotals(newItems);
    }

    case 'ADD_PROMOTION': {
      const newItems: CartItem[] = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, promotions: action.payload.promotions } // agora é só um objeto
          : item
      );
      return calculateTotals(newItems);
    }

    case 'ADD_DATA': {
      return {
        ...state,
        data: {
          ...state.data, // preserva se já houver algo
          ...action.payload // atualiza com os novos dados
        }
      };
    }

    case 'ADD_PAYMENT': {
      const newItems = state.items.map(item =>
        item.id === action.payload.id
          ? {
            ...item,
            payments: [
              ...(item.payments || []),
              ...(action.payload.payments || []), // espalhando os itens
            ]
          }
          : item
      );
      return calculateTotals(newItems);
    }

    case 'REMOVE_PROMOTION': {
      const newItems: CartItem[] = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, promotions: undefined } // remove o campo
          : item
      );
      return calculateTotals(newItems);
    }

    case 'REMOVE_PAYMENT': {
      const newItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, payments: [] } // ✅ Remove todas promoções
          : item
      );
      return calculateTotals(newItems);
    }

    case 'CLEAR_CART':
      return initialState;

    default:
      return state;
  }
}

// Context
interface CartContextType {
  state: CartState;
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  addPromotionToProduct: (productId: string, promotions: any) => void;
  removePromotionFromProduct: (productId: string) => void;
  addPaymentsToProduct: (productId: string, payments: any) => void;
  removePaymentsFromProduct: (productId: string) => void;
  clearCart: () => void;
  setData: (payload: IDataOrder) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Provider
interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  const addToCart = (product: Product) => {
    dispatch({ type: 'ADD_TO_CART', payload: product });
  };

  const removeFromCart = (productId: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const addPromotionToProduct = (productId: string, promotions: any) => {
    dispatch({ type: 'ADD_PROMOTION', payload: { id: productId, promotions } });
  };

  const removePromotionFromProduct = (productId: string) => {
    dispatch({ type: 'REMOVE_PROMOTION', payload: { id: productId } });
  };

  const addPaymentsToProduct = (productId: string, payments: any) => {
    dispatch({ type: 'ADD_PAYMENT', payload: { id: productId, payments } });
  };

  const removePaymentsFromProduct = (productId: string) => {
    dispatch({ type: 'REMOVE_PAYMENT', payload: { id: productId } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const setData = (partialData: Partial<IDataOrder>) => {
    dispatch({
      type: 'ADD_DATA',
      payload: partialData
    });
  };


  return (
    <CartContext.Provider value={{
      state,
      addToCart,
      removeFromCart,
      updateQuantity,
      addPromotionToProduct,
      removePromotionFromProduct,
      addPaymentsToProduct,
      removePaymentsFromProduct,
      clearCart,
      // setCustomer,
      setData
    }}>
      {children}
    </CartContext.Provider>
  );
}

// Hook customizado
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart deve ser usado dentro de um CartProvider');
  }
  return context;
}